import React, { useState, useEffect } from 'react';
import eventAPI from '../services/eventAPI';
import './ImageLightbox.css';

function ImageLightbox({ images, currentIndex, onClose, eventId }) {
  const [index, setIndex] = useState(currentIndex);
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionValue, setCaptionValue] = useState('');
  const [savingCaption, setSavingCaption] = useState(false);

  const currentImage = images[index];

  useEffect(() => {
    setIndex(currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    if (currentImage) {
      setCaptionValue(currentImage.caption || '');
    }
  }, [currentImage]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [index, images.length]);

  const handlePrevious = () => {
    setIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setEditingCaption(false);
  };

  const handleNext = () => {
    setIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setEditingCaption(false);
  };

  const handleSaveCaption = async () => {
    if (!currentImage) return;

    try {
      setSavingCaption(true);
      await eventAPI.updateImageCaption(eventId, currentImage.id, captionValue);
      // Update the image object
      currentImage.caption = captionValue;
      setEditingCaption(false);
    } catch (error) {
      console.error('Failed to save caption:', error);
      alert('Caption konnte nicht gespeichert werden');
    } finally {
      setSavingCaption(false);
    }
  };

  const handleCancelEdit = () => {
    setCaptionValue(currentImage?.caption || '');
    setEditingCaption(false);
  };

  if (!currentImage) return null;

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="lightbox-close" onClick={onClose}>
          ✕
        </button>

        {/* Image Counter */}
        <div className="lightbox-counter">
          [{index + 1}/{images.length}]
        </div>

        {/* Navigation Buttons */}
        {images.length > 1 && (
          <>
            <button className="lightbox-nav lightbox-nav-prev" onClick={handlePrevious}>
              ◄
            </button>
            <button className="lightbox-nav lightbox-nav-next" onClick={handleNext}>
              ►
            </button>
          </>
        )}

        {/* Image */}
        <div className="lightbox-image-container">
          <img
            src={eventAPI.getImageUrl(currentImage.file_path)}
            alt={currentImage.filename}
            className="lightbox-image"
          />
        </div>

        {/* Caption Section */}
        <div className="lightbox-caption-section">
          {editingCaption ? (
            <div className="lightbox-caption-edit">
              <input
                type="text"
                value={captionValue}
                onChange={(e) => setCaptionValue(e.target.value)}
                placeholder="Caption hinzufügen..."
                className="lightbox-caption-input"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveCaption();
                  } else if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
              />
              <div className="lightbox-caption-actions">
                <button
                  className="lightbox-btn lightbox-btn-save"
                  onClick={handleSaveCaption}
                  disabled={savingCaption}
                >
                  {savingCaption ? 'SPEICHERT...' : 'SPEICHERN'}
                </button>
                <button
                  className="lightbox-btn lightbox-btn-cancel"
                  onClick={handleCancelEdit}
                  disabled={savingCaption}
                >
                  ABBRECHEN
                </button>
              </div>
            </div>
          ) : (
            <div className="lightbox-caption-display">
              {currentImage.caption ? (
                <p className="lightbox-caption-text">"{currentImage.caption}"</p>
              ) : (
                <p className="lightbox-caption-empty">Keine Caption</p>
              )}
              <button
                className="lightbox-caption-edit-btn"
                onClick={() => setEditingCaption(true)}
                title="Caption bearbeiten"
              >
                [✎]
              </button>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lightbox-info">
          <span className="lightbox-uploader">Von: {currentImage.username}</span>
          <span className="lightbox-date">
            {new Date(currentImage.created_at).toLocaleDateString('de-DE')}
          </span>
        </div>
      </div>
    </div>
  );
}

export default ImageLightbox;
